import type { CellContext, ColumnDefTemplate } from '@tanstack/react-table';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useAppDispatch } from '@/redux/hooks';
import { updateDraftRow } from '@/redux/slices/table/slice';
import { cn } from '@/shared/utils';
import type { Hazard } from '@/types/swms';

// NOTE: Using important here as we have preflight styles from odin-components/myosh-login leaking and breaking the inputs
// This should be investigated and fixed so we don't have to use the 'important' modifier
const cellBaseClassNames = 'appearance-none rounded-sm border! p-1! text-sm focus:outline-hidden';
const textAreaClassNames = cn(cellBaseClassNames, 'flex w-full flex-1 resize-none! border-gray-4!');
const divClassNames = cn(cellBaseClassNames, 'min-h-[70px] border-transparent! whitespace-pre');

interface TextAreaCellInternalProps extends CellContext<Hazard, string[]> {
  editMode?: boolean;
}

const TextAreaCell: ColumnDefTemplate<TextAreaCellInternalProps> = ({ getValue, column, row, editMode }) => {
  const initialValue = getValue();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dispatch = useAppDispatch();
  const columnMeta = column.columnDef.meta;

  useEffect(() => {
    if (editMode && inputRef.current && initialValue.length > 0) {
      inputRef.current.value = initialValue.join('\n');
    }
  }, [initialValue, editMode]);

  const handleOnBlur = useCallback(() => {
    if (!editMode || !inputRef.current) {
      return;
    }

    const newValue = inputRef.current.value.trim();
    if (newValue.length > 0) {
      const inputValue = newValue.split('\n');
      if (inputValue.length !== initialValue.length || inputValue.some((val, index) => val !== initialValue[index])) {
        dispatch(updateDraftRow({ data: { [column.id]: inputValue }, rowIndex: row.index }));
      }
    }
  }, [editMode, initialValue, dispatch, column.id, row.index]);

  return editMode ? (
    <textarea
      name={`row-${row.id}-column-${column.id}`}
      ref={inputRef}
      onBlur={handleOnBlur}
      rows={3}
      placeholder={columnMeta?.placeholder ?? ''}
      className={textAreaClassNames}
    />
  ) : (
    <div className={divClassNames}>{initialValue.join('\n')}</div>
  );
};

export default memo(TextAreaCell);
